import React, { useEffect, useState } from "react";
import { RefreshCw, ShieldCheck, ShieldAlert, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { PageHeader, Card, Loading, Pill, SeverityTag, EmptyState } from "@/components/kit";

const STATUS_META = {
  Healthy: { icon: ShieldCheck, color: "#7CD992" },
  Warning: { icon: Shield, color: "#E7C25B" },
  Critical: { icon: ShieldAlert, color: "#FF6B6B" },
};

export default function DataHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");

  const load = async () => {
    setLoading(true);
    try { setData(await api.ownerDataHealth()); } catch { setData(null); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const meta = STATUS_META[data?.status] || STATUS_META.Healthy;
  const StatusIcon = meta.icon;
  const issues = (data?.issues || []).filter((i) => filter === "ALL" || i.severity === filter);

  return (
    <div>
      <PageHeader eyebrow="Owner · Read-only" title="Data Health Check"
        subtitle="Diagnostik data keuangan & operasional. Alat ini hanya mendeteksi & melaporkan — tidak pernah mengubah data."
        right={<Pill onClick={load}><RefreshCw className="mr-1 inline h-3.5 w-3.5" /> Periksa Ulang</Pill>}
      />

      {loading ? <Loading label="Menganalisis data..." /> : !data ? (
        <Card>Gagal menjalankan diagnostik.</Card>
      ) : (
        <div className="space-y-6">
          <Card className="flex flex-wrap items-center justify-between gap-4" style={{ borderColor: meta.color + "44" }}>
            <div className="flex items-center gap-4">
              <StatusIcon className="h-9 w-9" style={{ color: meta.color }} />
              <div>
                <div className="font-display text-2xl text-white" style={{ color: meta.color }}>{data.status}</div>
                <div className="text-xs text-[#6E6E73]">Terakhir diperiksa {formatDateTime(data.last_checked)}</div>
              </div>
            </div>
            <div className="flex gap-6">
              {["CRITICAL", "WARNING", "INFO"].map((s) => (
                <div key={s} className="text-center">
                  <div className="font-display text-2xl text-white">{data.counts?.[s] || 0}</div>
                  <div className="mt-1"><SeverityTag value={s} /></div>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex flex-wrap gap-2">
            {["ALL", "CRITICAL", "WARNING", "INFO"].map((s) => (
              <Pill key={s} active={filter === s} onClick={() => setFilter(s)}>{s === "ALL" ? "Semua" : s}</Pill>
            ))}
          </div>

          {!issues.length ? (
            <EmptyState icon={ShieldCheck} title="Tidak ada masalah" subtitle="Semua data konsisten pada kategori ini." />
          ) : (
            <div className="space-y-3">
              {issues.map((i) => (
                <Card key={i.id} className="fade-in" data-testid="health-issue">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <SeverityTag value={i.severity} />
                        <span className="text-[11px] uppercase tracking-widest text-[#6E6E73]">{i.category} · {i.type}</span>
                      </div>
                      <div className="mt-2 text-sm text-white">{i.description}</div>
                      {i.detail && Object.keys(i.detail).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#8E8E93]">
                          {Object.entries(i.detail).map(([k, v]) => (
                            <span key={k}><span className="text-[#5A5A5F]">{k}:</span> {String(Array.isArray(v) ? v.join(", ") : v)}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <code className="shrink-0 rounded bg-[#111] px-2 py-1 text-[11px] text-[#5A5A5F]">{String(i.ref).slice(0, 12)}</code>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
